/* Timeline panel — grouped change history, reversibility badges, click-to-diff, live refresh */

import { esc, setPanelLoading } from "./state.js";
import { api } from "./api.js";
import { showRawDiff } from "./diff-panel.js";
import { openUndoDialog } from "./rewind-dialog.js";

// ---------------------------------------------------------------------------
// Icon class mapping per operation
// ---------------------------------------------------------------------------

const OP_ICON = {
  CREATE: "ic-plus",
  UPDATE: "ic-edit",
  DELETE: "ic-trash",
};

const REV_CLASS = {
  reversible:   "rev-ok",
  risky:        "rev-risk",
  irreversible: "rev-no",
};

const REV_LABEL = {
  reversible:   "Reversible",
  risky:        "Risky",
  irreversible: "Record-only",
};

// Active filters — updated by the filter controls; applied on every rewindTimeline call.
let _filters = {};

// Stable accumulation of all known families — union-extended from every unfiltered load
// so that a filtered response (which narrows the groups) does not shrink the dropdown.
let _allFamilies = new Set();

// ---------------------------------------------------------------------------
// Container helper
// ---------------------------------------------------------------------------

function _container() {
  return document.getElementById("panel-timeline");
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function initTimelinePanel() {
  const el = _container();
  if (!el || el._tlWired) return;
  el._tlWired = true;
  el.addEventListener("click", _onClick);

  // Inject the compact filter row between .tl-head and #timelineBody.
  // Uses inline styles (with CSS custom properties) so no extra stylesheet is needed.
  const tlHead = el.querySelector(".tl-head");
  if (tlHead && !el.querySelector(".tl-filter-row")) {
    const CTL = [
      "font-size:12px;padding:2px 6px;border:1px solid var(--border);",
      "border-radius:var(--radius-sm);background:var(--surface);color:var(--text);",
      "font-family:var(--sans);",
    ].join("");
    const BTN = [
      "display:inline-flex;align-items:center;gap:4px;font-size:12px;",
      "padding:3px 9px;background:var(--sap-blue-soft);color:var(--sap-blue);",
      "border:1px solid var(--sap-blue-ring);border-radius:var(--radius-sm);",
      "cursor:pointer;font-family:var(--sans);",
    ].join("");

    tlHead.insertAdjacentHTML("afterend", `
      <div class="tl-filter-row" style="display:flex;gap:6px;padding:5px 10px;align-items:center;border-bottom:1px solid var(--border);flex-shrink:0;flex-wrap:wrap;background:var(--panel);">
        <select id="tlFamilyFilter" title="Filter by family" style="${CTL}">
          <option value="">All families</option>
        </select>
        <input type="date" id="tlSinceDate" title="From date (inclusive)" style="${CTL}" />
        <input type="date" id="tlUntilDate" title="To date (inclusive)" style="${CTL}" />
        <button id="tlDownloadCsv" title="Download audit CSV with active filters" style="${BTN}">
          <i class="ic ic-sm ic-download"></i> CSV
        </button>
      </div>
    `);

    // Wire filter controls: any change re-fetches from the server with the new filters.
    const familySel = el.querySelector("#tlFamilyFilter");
    const sinceIn   = el.querySelector("#tlSinceDate");
    const untilIn   = el.querySelector("#tlUntilDate");
    const csvBtn    = el.querySelector("#tlDownloadCsv");

    if (familySel) {
      familySel.addEventListener("change", () => {
        _filters.family = familySel.value || undefined;
        refreshTimeline();
      });
    }
    if (sinceIn) {
      sinceIn.addEventListener("change", () => {
        _filters.since = sinceIn.value || undefined;
        refreshTimeline();
      });
    }
    if (untilIn) {
      untilIn.addEventListener("change", () => {
        _filters.until = untilIn.value || undefined;
        refreshTimeline();
      });
    }
    if (csvBtn) {
      csvBtn.addEventListener("click", () => {
        api.downloadAuditCsv(_filters).catch((e) => console.error("CSV download:", e));
      });
    }
  }
}

export function onTimelineActivated() {
  refreshTimeline();
}

export async function refreshTimeline() {
  const el = _container();
  if (!el) return;
  const body = el.querySelector("#timelineBody");
  if (!body) return;

  setPanelLoading("timeline", true);
  try {
    // api.auditEntries routes to hosted (/api/connections/{cid}/audit → {entries:[...]})
    // or local (rewindTimeline → {groups:[...]}) based on auth mode.
    const data = await api.auditEntries(_filters);
    let groups;
    if (data && Array.isArray(data.entries)) {
      // Hosted mode: flat entry list — adapt to the groups shape _render expects.
      // has_diff comes from the server (list_entries now includes it as a list-only
      // field); the per-entry diff is served by GET /api/connections/{cid}/audit/{entry_id}.
      const entries = data.entries.map(e => ({
        id: e.id,
        op: e.op || "UPDATE",
        family: e.family || "",
        target_label: e.target || "",
        reversibility: e.reversibility || "irreversible",
        has_diff: e.has_diff === true,
        reverse_status: e.reverse_status || "pending",
      }));
      // On unfiltered load, grow the stable family set — never shrink it.
      if (!_filters.family) {
        for (const e of entries) if (e.family) _allFamilies.add(e.family);
      }
      const rollup = { reversible: 0, risky: 0, irreversible: 0 };
      for (const e of entries) if (rollup[e.reversibility] !== undefined) rollup[e.reversibility]++;
      groups = entries.length ? [{
        group_id: "hosted-audit",
        label: "Hosted audit trail",
        rollup,
        entries,
      }] : [];
    } else {
      // Local mode: {groups:[...]} from rewindTimeline.
      groups = (data && data.groups) ? data.groups : [];
      // On unfiltered load, grow the stable family set — never shrink it.
      if (!_filters.family) {
        for (const g of groups) {
          for (const e of (g.entries || [])) {
            if (e.family) _allFamilies.add(e.family);
          }
        }
      }
    }
    body.innerHTML = _render(groups);
    _updateFamilySelect(el, [..._allFamilies].sort());
  } catch (e) {
    body.innerHTML = `<div class="tl-empty">Could not load history: ${esc(String(e.message || e))}</div>`;
  } finally {
    setPanelLoading("timeline", false);
  }
}

// Live entry arrived — debounced refresh so rapid writes don't thrash
let _liveTimer;
export function addTimelineEntry() {
  clearTimeout(_liveTimer);
  _liveTimer = setTimeout(refreshTimeline, 200);
}

// ---------------------------------------------------------------------------
// Family select update — rebuild options from the loaded entries, preserving
// the current selection. Called after every rewindTimeline response so the
// family list always reflects what was returned (server-side filtered or full).
// ---------------------------------------------------------------------------

function _updateFamilySelect(el, sortedFamilies) {
  const sel = el.querySelector("#tlFamilyFilter");
  if (!sel) return;

  const currentVal = sel.value;
  const opts = ['<option value="">All families</option>'];
  for (const f of sortedFamilies) {
    opts.push(`<option value="${esc(f)}"${f === currentVal ? " selected" : ""}>${esc(f)}</option>`);
  }
  sel.innerHTML = opts.join("");
}

// ---------------------------------------------------------------------------
// Rendering helpers
// ---------------------------------------------------------------------------

function _badge(rev) {
  const cls = REV_CLASS[rev] || "rev-no";
  const lbl = REV_LABEL[rev] || rev;
  return `<span class="rev-badge ${esc(cls)}"><span class="rev-dot"></span>${esc(lbl)}</span>`;
}

function _render(groups) {
  if (!groups.length) {
    return `<div class="tl-empty">No changes recorded yet.</div>`;
  }

  return groups.map(g => {
    const r = g.rollup || { reversible: 0, risky: 0, irreversible: 0 };
    const rows = (g.entries || []).map(e => {
      const iconCls = OP_ICON[e.op] || "ic-edit";
      const isDone = e.reverse_status === "reversed";
      const badgeHtml = isDone
        ? `<span class="tl-done">undone</span>`
        : _badge(e.reversibility);
      return `<div class="tl-row" data-entry="${esc(e.id)}" data-rev="${esc(e.reversibility)}" data-has-diff="${e.has_diff ? 1 : 0}" data-family="${esc(e.family || "")}">
        <i class="ic ic-sm ${esc(iconCls)}"></i>
        <span class="tl-tgt">${esc(e.op.toLowerCase())} <b>${esc(e.target_label || e.family || "")}</b></span>
        ${badgeHtml}
        <span class="tl-acts">
          ${e.has_diff ? `<span class="tl-a" data-act="diff" title="View diff"><i class="ic ic-sm ic-eye"></i></span>` : ""}
          <span class="tl-a" data-act="undo-one" title="Undo just this"><i class="ic ic-sm ic-undo"></i></span>
          <span class="tl-a" data-act="undo-here" title="Undo this and everything after"><i class="ic ic-sm ic-rewind"></i></span>
        </span>
      </div>`;
    }).join("");

    return `<div class="tl-group" data-group="${esc(g.group_id)}">
      <div class="tl-ghead">
        <i class="ic ic-sm ic-zap"></i>
        <span class="tl-nm">${esc(g.label || g.group_id)}</span>
        <span class="tl-cnt">${(g.entries || []).length} &middot; ${r.reversible || 0}&check; ${r.risky || 0}! ${r.irreversible || 0}&middot;</span>
        <button class="tl-undo-task" data-act="undo-task"><i class="ic ic-sm ic-undo"></i> Undo task</button>
      </div>
      ${rows}
    </div>`;
  }).join("");
}

// ---------------------------------------------------------------------------
// Click handler
// ---------------------------------------------------------------------------

async function _onClick(ev) {
  const a = ev.target.closest(".tl-a, .tl-undo-task");
  const row = ev.target.closest(".tl-row");
  const group = ev.target.closest(".tl-group");

  if (!a && row) {
    // Bare row click → open diff, but only if this entry has a before/after image
    if (row.dataset.hasDiff === "1") {
      await _openDiff(row.dataset.entry);
    }
    return;
  }
  if (!a) return;

  const act = a.dataset.act;

  if (act === "diff" && row) {
    await _openDiff(row.dataset.entry);
    return;
  }
  if (act === "undo-one" && row) {
    // openUndoDialog routes to the hosted undo endpoint in hosted mode (via api.undoPreview /
    // api.undoExecute) or falls back to the local rewind tool — no timeline-side routing needed.
    openUndoDialog({ entry_ids: [row.dataset.entry] }, refreshTimeline);
    return;
  }
  if (act === "undo-here" && row) {
    openUndoDialog({ upto_entry_id: row.dataset.entry }, refreshTimeline);
    return;
  }
  if (act === "undo-task" && group) {
    openUndoDialog({ group_id: group.dataset.group }, refreshTimeline);
    return;
  }
}

async function _openDiff(entryId) {
  if (!entryId) return;
  try {
    // auditEntry routes to hosted GET /api/connections/{cid}/audit/{entry_id} in hosted
    // mode, or to the local rewindEntry tool in local mode. Both return {op, family, diff}.
    const e = await api.auditEntry(entryId);
    const title = `${(e.op || "").toLowerCase()} ${e.family || ""}`.trim();
    showRawDiff({ title: title || "change diff", unifiedDiff: e.diff || "(no diff available)" });
  } catch (err) {
    console.error("diff", err);
  }
}
